import { Injectable, computed, signal } from '@angular/core';
import { Subscription } from 'rxjs';
import { environment } from '../../environments/environment';
import { ChequeLockState } from '../models/pay-at-table.model';
import { isChequeEvent } from '../models/websocket.model';
import { ApiService } from './api.service';
import { DataService } from './data.service';
import { TableSocketService } from './table-socket.service';

const pollIntervalMs = 3500;

// PAT payment lock: first guest on the page holds it, others are read-only.
// The poll doubles as a presence heartbeat so the backend can free a lock
// abandoned by a dead/closed phone.
@Injectable({
  providedIn: 'root'
})
export class ChequeSyncService {

  public lock = signal<ChequeLockState | null>(null);

  public isHolder = computed(
    () => !!this.lock() && this.lock()?.locked_by_user_id === this.dataService.getUserId()
  );
  public lockedByOther = computed(() => {
    const lock = this.lock();
    return !!lock?.locked_by_user_id && lock.locked_by_user_id !== this.dataService.getUserId();
  });

  private pollTimer: ReturnType<typeof setInterval> | null = null;
  private pollInFlight = false;
  private acquireInFlight = false;
  private flowDepth = 0;
  // True during a confirm/redirect so leaving the page doesn't release the lock mid-payment.
  private paymentInFlight = false;
  // True if we left the payment page still holding the lock through an in-flight payment.
  private heldThroughPayment = false;
  private chequeEventSubscription: Subscription | null = null;
  private readonly onVisibilityChange = (): void => {
    if (!document.hidden && this.isActive()) {
      this.refresh();
    }
  };
  private readonly onPageHide = (): void => {
    if (this.isActive() && this.isHolder() && !this.paymentInFlight) {
      this.beaconRelease();
    }
  };

  constructor(
    private apiService: ApiService,
    private dataService: DataService,
    private tableSocketService: TableSocketService,
  ) {
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    window.addEventListener('pagehide', this.onPageHide);
  }

  public isEnabled(): boolean {
    return !!this.dataService.restaurant()?.pat_cheque_sync_enabled;
  }

  public isActive(): boolean {
    return this.pollTimer !== null;
  }

  // Refcounted so navigating payments <-> split keeps the poll/lock alive.
  public enterPaymentFlow(): void {
    if (!this.isEnabled()) {
      return;
    }
    this.flowDepth += 1;
    if (this.flowDepth === 1) {
      this.start();
    }
  }

  public leavePaymentFlow(): void {
    if (this.flowDepth === 0) {
      return;
    }
    this.flowDepth -= 1;
    if (this.flowDepth === 0) {
      // Deferred so payments <-> split navigation doesn't release-then-reacquire mid-transition.
      setTimeout(() => {
        if (this.flowDepth === 0 && this.isActive()) {
          this.stop();
        }
      }, 250);
    }
  }

  public markPaymentInFlight(value: boolean): void {
    this.paymentInFlight = value;
    if (value) {
      this.heldThroughPayment = false;
    }
  }

  // Payment attempt ended without completing; release the lock now if we already left the page holding it.
  public abortPaymentFlow(): void {
    this.paymentInFlight = false;
    if (this.isActive()) {
      return;
    }
    if (this.heldThroughPayment) {
      this.heldThroughPayment = false;
      this.releaseLock();
    }
  }

  public refresh(): void {
    if (this.pollInFlight) {
      return;
    }
    this.pollInFlight = true;
    this.apiService.getChequeLock(this.tableId()).subscribe({
      next: (payload) => {
        this.pollInFlight = false;
        this.applyLock(payload.lock);
      },
      error: () => {
        this.pollInFlight = false;
      }
    });
  }

  private applyLock(lock: ChequeLockState | null): void {
    this.lock.set(lock);
    if (this.isActive() && !document.hidden && !lock?.locked_by_user_id) {
      this.acquireLock();
    }
  }

  private acquireLock(): void {
    if (this.acquireInFlight || this.flowDepth === 0) {
      return;
    }
    this.acquireInFlight = true;
    this.apiService
      .acquireChequeLock(this.tableId(), this.dataService.getUserId())
      .subscribe({
        next: (payload) => {
          this.acquireInFlight = false;
          this.lock.set(payload.lock);
        },
        error: (error) => {
          this.acquireInFlight = false;
          if (error?.status === 423 && error?.error?.lock) {
            this.lock.set(error.error.lock);
          }
        }
      });
  }

  // ---- Internals ----

  private tableId(): string {
    return this.dataService.table()?.id || '';
  }

  private start(): void {
    this.stopTimer();
    this.refresh();
    this.acquireLock();

    this.chequeEventSubscription?.unsubscribe();
    this.chequeEventSubscription = this.tableSocketService.message$.subscribe((event) => {
      if (!document.hidden && isChequeEvent(event) && event.table_id === this.tableId()) {
        this.refresh();
      }
    });

    this.pollTimer = setInterval(() => {
      if (document.hidden) {
        return;
      }
      this.refresh();
    }, pollIntervalMs);
  }

  private stop(): void {
    const isHolder = this.isHolder();
    const shouldRelease = isHolder && !this.paymentInFlight;
    // So abortPaymentFlow() can release it later if this payment gets cancelled.
    this.heldThroughPayment = isHolder && this.paymentInFlight;
    this.chequeEventSubscription?.unsubscribe();
    this.chequeEventSubscription = null;
    this.stopTimer();
    if (shouldRelease) {
      this.releaseLock();
    }
    this.lock.set(null);
  }

  private releaseLock(): void {
    this.apiService
      .releaseChequeLock(this.tableId(), this.dataService.getUserId())
      .subscribe({ next: () => undefined, error: () => undefined });
  }

  private stopTimer(): void {
    if (this.pollTimer !== null) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }

  private beaconRelease(): void {
    const body = new Blob(
      [JSON.stringify({ user_id: this.dataService.getUserId() })],
      { type: 'text/plain' }
    );
    navigator.sendBeacon(
      `${environment.urls.api}/cheque/table/${this.tableId()}/lock/release`,
      body
    );
  }

}
